import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis,
  Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import {
  TrendingUp, TrendingDown, Activity, ChevronRight, Sparkles, Bell, Send,
  RefreshCw, BarChart3, Compass,
} from "lucide-react";
import HedgingCopilotPanel from "@/components/HedgingCopilotPanel";

// Phase 36 — Commodity Index Linkage
// Pure presentation / wiring; all math comes from /api/commodities/*

const RANGES = [
  { key: "1m", label: "1M" },
  { key: "3m", label: "3M" },
  { key: "6m", label: "6M" },
  { key: "1y", label: "1Y" },
  { key: "3y", label: "3Y" },
];

const fmtPrice = (n, unit = "") => {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000) return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (abs >= 100) return n.toFixed(1);
  return n.toFixed(2);
};

const fmtPct = (n) => {
  if (n === null || n === undefined) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

const MOMENTUM_COLOR = {
  "STRONG-BULL": "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10",
  "BULL": "text-[#00E676] border-[#00E676]/30 bg-[#00E676]/5",
  "FLAT": "text-zinc-400 border-zinc-700 bg-zinc-900/40",
  "BEAR": "text-[#FF6B47] border-[#FF6B47]/30 bg-[#FF6B47]/5",
  "STRONG-BEAR": "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10",
};

export default function Commodities() {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [series, setSeries] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [range, setRange] = useState("1y");
  const [outlook, setOutlook] = useState(null);
  const [outlookLoading, setOutlookLoading] = useState(false);
  const [alertCfg, setAlertCfg] = useState(null);
  const [savingAlert, setSavingAlert] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);

  // Auto-scroll to Hedging Co-Pilot when arriving via #hedging-copilot anchor
  // (e.g. from Hedge Ledger empty-state CTA). Pulse the panel border briefly.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#hedging-copilot") return;
    // Wait a tick for the panel to mount + selectedMeta to populate.
    const t = setTimeout(() => {
      const el = document.getElementById("hedging-copilot");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-[#FFB000]", "ring-offset-0");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#FFB000]"), 2400);
    }, 600);
    return () => clearTimeout(t);
  }, [selectedId]);

  // initial list load
  useEffect(() => {
    api.get("/commodities").then(r => {
      setList(r.data || []);
      // pick the most "newsworthy" by default — the first item (already sorted by 30d move)
      if ((r.data || []).length && !selectedId) setSelectedId(r.data[0].id);
    }).catch(() => toast.error("Failed to load commodity catalogue"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // selection / range change → refetch series
  useEffect(() => {
    if (!selectedId) return;
    setSeries(null);
    setForecast(null);
    setOutlook(null);
    Promise.all([
      api.get(`/commodities/${selectedId}/series?range=${range}`),
      api.get(`/commodities/${selectedId}/forecast?horizon=126`).catch(() => ({ data: null })),
      api.get(`/commodities/${selectedId}/alerts/config`).catch(() => ({ data: null })),
    ]).then(([s, f, a]) => {
      setSeries(s.data);
      setForecast(f.data);
      setAlertCfg(a.data);
    });
  }, [selectedId, range]);

  const selectedMeta = useMemo(
    () => list.find(c => c.id === selectedId),
    [list, selectedId],
  );

  const chartData = useMemo(() => {
    if (!series) return [];
    const past = (series.points || []).map(p => ({ date: p.date, history: p.close }));
    const fwd = (forecast?.points || []).map(p => ({ date: p.date, projection: p.close }));
    return [...past, ...fwd];
  }, [series, forecast]);

  const generateOutlook = async () => {
    if (!selectedId) return;
    setOutlookLoading(true);
    try {
      const r = await api.post(`/commodities/${selectedId}/outlook`);
      setOutlook(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Outlook generation failed");
    } finally {
      setOutlookLoading(false);
    }
  };

  const saveAlertCfg = async (patch) => {
    if (!selectedId) return;
    setSavingAlert(true);
    try {
      await api.patch(`/commodities/${selectedId}/alerts/config`, patch);
      const r = await api.get(`/commodities/${selectedId}/alerts/config`);
      setAlertCfg(r.data);
      toast.success("Alert config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAlert(false);
    }
  };

  const sendTestAlert = async () => {
    if (!selectedId) return;
    setTestingAlert(true);
    try {
      const r = await api.post(`/commodities/${selectedId}/alerts/test`);
      if (r.data.delivered) toast.success(`Test ping sent to ${r.data.channel}`);
      else toast.warning(`Posted in log mode — install Slack to deliver to ${r.data.channel}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestingAlert(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="commodities-page">
      {/* HEADER */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            // GLOBAL COMMODITY DESK · PLATTS / LME / LBMA SIM
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1">
            Commodity Indices
          </h1>
          <p className="text-xs text-zinc-500 max-w-xl mt-1.5">
            Synthetic real-time price benchmarks for the world's most-traded mining,
            shipping & energy commodities. All series deterministic & reproducible.
          </p>
        </div>
        <a
          href="#hedging-copilot"
          data-testid="jump-to-hedge-copilot"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById("hedging-copilot");
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
              el.classList.add("ring-2", "ring-[#FFB000]");
              setTimeout(() => el.classList.remove("ring-2", "ring-[#FFB000]"), 1800);
            }
          }}
          className="inline-flex items-center gap-2 border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 px-3 py-2 font-mono text-[11px] uppercase tracking-widest"
        >
          <Compass className="w-3.5 h-3.5" /> JUMP TO HEDGE CO-PILOT ↓
        </a>
      </div>

      {/* TILE GRID — all commodities */}
      <Panel
        title="// MARKET BOARD"
        meta={`${list.length} BENCHMARKS · UPDATED ${new Date().toUTCString().slice(5, 22)} UTC`}
        testId="commodities-grid"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-zinc-900">
          {list.map(c => {
            const isSel = c.id === selectedId;
            const ch30 = c.stats?.change_30d_pct;
            const up = (ch30 ?? 0) >= 0;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                data-testid={`commodity-tile-${c.id}`}
                className={`relative p-4 text-left transition-colors bg-black hover:bg-zinc-950 ${
                  isSel ? "ring-2 ring-[#FFB000] ring-inset z-10" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 truncate">
                      {c.benchmark}
                    </div>
                    <div className="font-heading font-black text-sm tracking-tight mt-0.5">
                      {c.short}
                    </div>
                  </div>
                  <span
                    className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: c.color }}
                  />
                </div>
                <div className="mt-3 flex items-end justify-between gap-2">
                  <div>
                    <div className="font-mono text-2xl font-medium text-white">
                      {fmtPrice(c.stats?.spot)}
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">
                      {c.unit}
                    </div>
                  </div>
                  <div className={`text-right`}>
                    <div className={`font-mono text-xs flex items-center gap-1 ${up ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {fmtPct(ch30)}
                    </div>
                    <div className="font-mono text-[9px] text-zinc-600 mt-0.5">30D</div>
                  </div>
                </div>
                <div className="mt-3">
                  <span
                    className={`inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${
                      MOMENTUM_COLOR[c.stats?.momentum] || MOMENTUM_COLOR.FLAT
                    }`}
                  >
                    {c.stats?.momentum || "FLAT"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </Panel>

      {/* DETAIL — selected commodity */}
      {selectedMeta && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main chart panel — 2 cols on lg */}
          <div className="lg:col-span-2 space-y-5">
            <Panel
              title={`// ${selectedMeta.short} · ${selectedMeta.benchmark}`}
              meta={`UNIT ${selectedMeta.unit}`}
              testId="commodity-chart-panel"
            >
              <div className="px-4 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-1">
                  {RANGES.map(r => (
                    <button
                      key={r.key}
                      onClick={() => setRange(r.key)}
                      data-testid={`range-${r.key}`}
                      className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border transition-colors ${
                        range === r.key
                          ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                          : "border-zinc-800 text-zinc-500 hover:text-white"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <div className="font-mono text-[10px] text-zinc-600">
                  REGULATOR · {selectedMeta.regulator}
                </div>
              </div>

              {!series && (
                <div className="h-[320px] flex items-center justify-center font-mono text-xs text-zinc-600">
                  Loading series…
                </div>
              )}

              {series && (
                <div className="h-[320px] min-h-[320px] px-2">
                  <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={320}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="histFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={selectedMeta.color} stopOpacity={0.45} />
                          <stop offset="100%" stopColor={selectedMeta.color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="2 4" stroke="#27272a" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                        tickFormatter={(d) => d.slice(2, 7)}
                        minTickGap={32}
                        stroke="#3f3f46"
                      />
                      <YAxis
                        tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                        domain={["auto", "auto"]}
                        stroke="#3f3f46"
                        width={64}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#000", border: "1px solid #3f3f46",
                          fontFamily: "monospace", fontSize: 11,
                        }}
                        labelStyle={{ color: "#FFB000" }}
                        formatter={(v) => v?.toFixed?.(2) ?? v}
                      />
                      {series.stats?.support && (
                        <ReferenceLine
                          y={series.stats.support}
                          stroke="#00E676"
                          strokeDasharray="3 3"
                          label={{
                            value: "SUPPORT", position: "left",
                            fill: "#00E676", fontSize: 9, fontFamily: "monospace",
                          }}
                        />
                      )}
                      {series.stats?.resistance && (
                        <ReferenceLine
                          y={series.stats.resistance}
                          stroke="#FF3B30"
                          strokeDasharray="3 3"
                          label={{
                            value: "RESISTANCE", position: "left",
                            fill: "#FF3B30", fontSize: 9, fontFamily: "monospace",
                          }}
                        />
                      )}
                      <Area
                        type="monotone"
                        dataKey="history"
                        stroke={selectedMeta.color}
                        strokeWidth={1.5}
                        fill="url(#histFill)"
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="projection"
                        stroke="#FFB000"
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Stats strip */}
              {series?.stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-900 border-t border-zinc-900">
                  {[
                    { label: "SPOT", value: fmtPrice(series.stats.spot), sub: selectedMeta.unit, accent: "white" },
                    { label: "30D", value: fmtPct(series.stats.change_30d_pct), accent: (series.stats.change_30d_pct ?? 0) >= 0 ? "green" : "red" },
                    { label: "90D", value: fmtPct(series.stats.change_90d_pct), accent: (series.stats.change_90d_pct ?? 0) >= 0 ? "green" : "red" },
                    { label: "1Y", value: fmtPct(series.stats.change_1y_pct), accent: (series.stats.change_1y_pct ?? 0) >= 0 ? "green" : "red" },
                  ].map(s => (
                    <div key={s.label} className="bg-black p-3">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{s.label}</div>
                      <div
                        className={`font-mono text-lg font-medium mt-0.5 ${
                          s.accent === "green" ? "text-[#00E676]"
                          : s.accent === "red" ? "text-[#FF3B30]" : "text-white"
                        }`}
                      >
                        {s.value}
                      </div>
                      {s.sub && <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{s.sub}</div>}
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* DARA outlook — on demand */}
            <Panel title="// DARA STRATEGIC OUTLOOK" testId="commodity-outlook-panel">
              {!outlook && (
                <div className="p-5 flex flex-col sm:flex-row items-start gap-4">
                  <Sparkles className="w-5 h-5 text-[#FFB000] mt-0.5" />
                  <div className="flex-1">
                    <div className="font-heading font-bold text-base mb-1">
                      Generate a board-ready 3-paragraph outlook
                    </div>
                    <p className="text-xs text-zinc-500 max-w-xl mb-3">
                      DARA reads the last 12 months of {selectedMeta.short.toLowerCase()} prices,
                      momentum, and your operating context, and writes a base / bull / bear case
                      with concrete drivers. Cached for 24h.
                    </p>
                    <button
                      onClick={generateOutlook}
                      disabled={outlookLoading}
                      data-testid="generate-outlook-btn"
                      className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                    >
                      {outlookLoading ? (
                        <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> WRITING…</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5" /> GENERATE OUTLOOK</>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {outlook && (
                <div className="p-5 space-y-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">SUMMARY</div>
                    <p className="text-sm text-zinc-300 leading-relaxed">{outlook.summary}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border-l-2 border-[#00E676] pl-3">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-1">BULL CASE</div>
                      <p className="text-xs text-zinc-300 leading-relaxed">{outlook.bull_case}</p>
                    </div>
                    <div className="border-l-2 border-zinc-600 pl-3">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">BASE CASE</div>
                      <p className="text-xs text-zinc-300 leading-relaxed">{outlook.base_case}</p>
                    </div>
                    <div className="border-l-2 border-[#FF3B30] pl-3">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] mb-1">BEAR CASE</div>
                      <p className="text-xs text-zinc-300 leading-relaxed">{outlook.bear_case}</p>
                    </div>
                  </div>
                  {outlook.drivers && outlook.drivers.length > 0 && (
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">KEY DRIVERS TO MONITOR</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {outlook.drivers.map((d, i) => (
                          <div key={i} className="flex items-start gap-2 font-mono text-[11px] text-zinc-400">
                            <ChevronRight className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" />
                            <span>{d}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="font-mono text-[9px] text-zinc-600 pt-2 border-t border-zinc-900">
                    Generated {new Date(outlook.generated_at).toLocaleString()} · cached 24h · DARA Engine
                  </div>
                </div>
              )}
            </Panel>
          </div>

          {/* Right rail — alerts + meta */}
          <div className="space-y-5">
            <Panel title="// PRICE ALERTS · SLACK" testId="commodity-alerts-panel">
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        alertCfg?.slack_connected ? "bg-[#00E676]" : "bg-zinc-700"
                      }`}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                      {alertCfg?.slack_connected
                        ? `SLACK · ${alertCfg.slack_team}`
                        : "SLACK NOT CONNECTED"}
                    </span>
                  </div>
                  <button
                    onClick={() => saveAlertCfg({ enabled: !alertCfg?.config?.enabled })}
                    disabled={savingAlert}
                    data-testid="alerts-toggle"
                    className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest border ${
                      alertCfg?.config?.enabled
                        ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5"
                        : "border-zinc-800 text-zinc-500"
                    }`}
                  >
                    {alertCfg?.config?.enabled ? "ALERTS ON" : "ALERTS OFF"}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">DROP %</span>
                    <input
                      type="number"
                      step="0.5"
                      defaultValue={alertCfg?.config?.drop_pct ?? 5}
                      onBlur={(e) => saveAlertCfg({ drop_pct: Number(e.target.value) })}
                      data-testid="alert-drop-pct"
                      className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">RISE %</span>
                    <input
                      type="number"
                      step="0.5"
                      defaultValue={alertCfg?.config?.rise_pct ?? 5}
                      onBlur={(e) => saveAlertCfg({ rise_pct: Number(e.target.value) })}
                      data-testid="alert-rise-pct"
                      className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">WINDOW (DAYS)</span>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    defaultValue={alertCfg?.config?.window_days ?? 7}
                    onBlur={(e) => saveAlertCfg({ window_days: Number(e.target.value) })}
                    data-testid="alert-window-days"
                    className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SLACK CHANNEL</span>
                  <input
                    type="text"
                    placeholder="#commodities"
                    defaultValue={alertCfg?.config?.slack_channel ?? ""}
                    onBlur={(e) => saveAlertCfg({ slack_channel: e.target.value })}
                    data-testid="alert-channel"
                    className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>

                <button
                  onClick={sendTestAlert}
                  disabled={testingAlert || !alertCfg?.slack_connected}
                  data-testid="send-test-alert"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 flex items-center justify-center gap-2"
                >
                  <Send className="w-3 h-3" />
                  {testingAlert ? "SENDING…" : "SEND TEST PING"}
                </button>

                {!alertCfg?.slack_connected && (
                  <div className="font-mono text-[10px] text-zinc-500 leading-relaxed border-l-2 border-[#FFB000]/40 pl-2 pt-1">
                    Connect Slack via Settings → Advanced → Integrations to receive automated price alerts.
                  </div>
                )}
              </div>
            </Panel>

            {/* Meta panel */}
            <Panel title="// BENCHMARK META" testId="commodity-meta-panel">
              <div className="p-4 space-y-2 text-xs">
                <Row label="NAME" value={selectedMeta.name} />
                <Row label="UNIT" value={selectedMeta.unit} />
                <Row label="CATEGORY" value={selectedMeta.category} />
                <Row label="REGULATOR" value={selectedMeta.regulator} />
                <Row label="INDUSTRIES" value={selectedMeta.industries.join(", ")} />
                {series?.stats && (
                  <>
                    <Row label="SUPPORT" value={`${fmtPrice(series.stats.support)} ${selectedMeta.unit}`} />
                    <Row label="RESISTANCE" value={`${fmtPrice(series.stats.resistance)} ${selectedMeta.unit}`} />
                    <Row label="MOMENTUM" value={series.stats.momentum} />
                    <Row label="SAMPLES" value={`${series.stats.samples} trading days`} />
                  </>
                )}
                <div className="pt-3 mt-2 border-t border-zinc-900 font-mono text-[9px] text-zinc-600 leading-relaxed">
                  Series simulated for demo / hedging-strategy purposes. Production
                  buyers should consult licensed Platts / LME / LBMA feeds before
                  pricing tonnes.
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Phase 36.1 — Hedging Co-Pilot */}
      {selectedMeta && (
        <div id="hedging-copilot" className="scroll-mt-20 transition-all duration-500">
          <HedgingCopilotPanel commodityId={selectedId} commodityMeta={selectedMeta} />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</span>
      <span className="font-mono text-[11px] text-zinc-300 text-right">{value}</span>
    </div>
  );
}
